import { CloudOff, HardDriveDownload, RefreshCw, Trash2, X } from 'lucide-react'
import { Link } from 'react-router'
import { useRecordingStore, formatElapsed, type PendingUpload } from '@/stores/recording'
import { useAuthStore } from '@/stores/auth'
import { confirm } from '@/components/shared/ConfirmDialog'
import { regionalText } from '@/lib/regional-copy'
import { jLocale } from '@/lib/jurisdiction'

/**
 * "Your consult audio is safe, but not submitted" (CLA-938 Phase 1).
 *
 * Before this, a failed commit reset the recording store to `idle` and the blob
 * went out of scope — the clinician got a red message (in a banner that is not
 * even mounted) and had no way back to their audio. Now the audio is on disk and
 * this is the affordance that says so and offers the retry.
 *
 * Two kinds of entry, deliberately rendered differently:
 *  - `retryable` — a transport or server failure. "Retry upload" is the action.
 *  - `plan_capped` — a 402 note_cap_reached. The audio is fine and the PLAN is
 *    out of included notes, so the action is an upgrade, not a retry. Offering
 *    "Retry upload" here would be a button that 402s every time it is pressed
 *    until billing changes. It gets "Choose a plan" instead: this is the moment
 *    of highest intent (a consult just recorded and no note to show for it), and
 *    until CLA-961 it was the one billing surface with no way forward at all —
 *    the clinician had to independently think of Settings.
 *
 * Mounted in AppShell's chrome branch beside the other banners, so it follows
 * the clinician across every page rather than living on the notes view — the
 * failure they need to act on shouldn't disappear because they navigated away.
 */
export function PendingUploadBanner() {
  const pendingUploads = useRecordingStore((s) => s.pendingUploads)
  const persistenceWarning = useRecordingStore((s) => s.persistenceWarning)
  const clearPersistenceWarning = useRecordingStore((s) => s.clearPersistenceWarning)

  if (pendingUploads.length === 0 && !persistenceWarning) return null

  return (
    <div className="shrink-0">
      {/* The one case where the audio really is at risk: no local backup. */}
      {persistenceWarning && (
        <div
          role="alert"
          className="bg-error/10 border-b border-error/25 px-4 py-2 flex items-center gap-3"
        >
          <CloudOff className="w-4 h-4 text-error shrink-0" aria-hidden />
          <p className="flex-1 text-[13px] text-on-error-soft">
            <span className="font-semibold">{persistenceWarning}</span>{' '}
            Keep this tab open until the upload finishes.
          </p>
          <button
            onClick={clearPersistenceWarning}
            className="shrink-0 p-1 rounded-lg text-on-error-soft hover:text-on-error-soft hover:bg-error/10 transition-colors"
            aria-label="Dismiss storage warning"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {pendingUploads.map((pending) => (
        <PendingUploadRow key={pending.id} pending={pending} />
      ))}
    </div>
  )
}

function PendingUploadRow({ pending }: { pending: PendingUpload }) {
  const retryUpload = useRecordingStore((s) => s.retryUpload)
  const discardPendingUpload = useRecordingStore((s) => s.discardPendingUpload)
  const canManage = useAuthStore((s) => s.canManageClinic)()

  const capped = pending.reason === 'plan_capped'
  // The browser can no longer read this recording back. Nothing can be
  // uploaded, so — like the cap — no retry button; unlike the cap there is no
  // way forward at all, and the honest thing is to stop implying there is.
  const unreadable = pending.reason === 'unreadable'
  const recordedAt = new Date(pending.recordedAt)
  const timeLabel = Number.isNaN(recordedAt.getTime())
    ? ''
    : recordedAt.toLocaleTimeString(jLocale(), { hour: 'numeric', minute: '2-digit' })

  const handleDiscard = async () => {
    // "Deleting it cannot be undone" is a warning about losing audio. When the
    // audio is already unreadable there is nothing left to lose, and saying so
    // would make the clinician hesitate over an entry that can only be cleared.
    const ok = await confirm(
      unreadable ? 'Clear this recording?' : 'Delete this recording?',
      unreadable
        ? {
            description:
              'This recording can no longer be opened, so nothing can be uploaded from it. Clearing it just removes the message.',
            confirmLabel: 'Clear',
          }
        : {
            description:
              'The audio is stored on this device and has not been submitted. Deleting it cannot be undone.',
            confirmLabel: 'Delete recording',
            variant: 'danger',
          },
    )
    if (ok) discardPendingUpload(pending.id)
  }

  return (
    <div className="bg-warning-light border-b border-warning/25 px-4 py-2 flex items-center gap-3">
      <HardDriveDownload className="w-4 h-4 text-warning shrink-0" aria-hidden />
      <p className="flex-1 text-[13px] text-on-warning-soft min-w-0">
        <span className="font-semibold">
          {capped
            ? 'A recording is saved on this device but your plan is out of notes.'
            : unreadable
              ? 'A recording on this device can no longer be opened.'
              : 'A recording is saved on this device but hasn’t been submitted.'}
        </span>{' '}
        <span className="text-on-warning-soft">
          {formatElapsed(pending.durationSeconds)}
          {timeLabel && ` recorded at ${timeLabel}`} — {pending.message}
          {/* An attempt count next to "can no longer be opened" reads as
              "keep trying". It is the opposite of the point. */}
          {!unreadable && pending.attempts > 1 && ` (${pending.attempts} attempts)`}
          {unreadable &&
            ' Your browser may have cleared it. Anything already uploaded is safe in your notes.'}
          {/* Someone who can't change the plan needs to know that, not a button
              that 403s. Same posture as UpgradePanel's non-manager branch. */}
          {capped && !canManage && regionalText(' Your clinic owner can choose a plan in Settings.')}
        </span>
      </p>

      {/* Deliberately no retry button on a plan cap: it cannot succeed until the
          plan changes, and a button that always fails teaches people to ignore
          this banner — which is the banner that protects their audio. The way
          forward is the plan chooser, which is a decision (Scribe vs Clinic,
          monthly vs annual) rather than a one-click upgrade — so this points at
          Settings rather than minting a checkout the clinician didn't choose. */}
      {capped && canManage && (
        <Link
          to="/settings/plan"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-claro-600 rounded-lg hover:bg-claro-700 transition-colors"
        >
          Choose a plan
        </Link>
      )}

      {!capped && !unreadable && (
        <button
          onClick={() => retryUpload(pending.id)}
          disabled={pending.retrying}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-claro-600 rounded-lg hover:bg-claro-700 disabled:bg-primary-disabled disabled:text-on-primary-disabled disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${pending.retrying ? 'animate-spin' : ''}`} />
          {pending.retrying ? 'Uploading…' : 'Retry upload'}
        </button>
      )}

      <button
        onClick={handleDiscard}
        disabled={pending.retrying}
        className="shrink-0 p-1.5 rounded-lg text-on-warning-soft hover:text-on-error-soft hover:bg-error/10 disabled:opacity-40 transition-colors"
        title="Delete this recording"
        aria-label="Delete this recording"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}
